import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Database, Newspaper, FileText, Building2, Users,
  RefreshCcw, Globe, Wrench
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDataOperations() {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getToken();
        const response = await fetch(`${API_URL}/api/analytics/data/operations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json();
          setData(result);
        }
      } catch (error) {
        console.error('Error fetching data operations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getToken]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="admin-data-ops-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  const newsIngestion = data?.news_ingestion || {};
  const tenderCollection = data?.tender_collection || {};
  const entityExtraction = data?.entity_extraction || {};
  // WO-36 — each source now carries {count, newest}: how many records, and when
  // the most recent one arrived (the freshness of that source).
  const sourceCount = (v) => (v && typeof v === 'object' ? v.count : v) || 0;
  const newestLabel = (v) => {
    const iso = v && typeof v === 'object' ? v.newest : null;
    if (!iso) return 'newest: —';
    const d = new Date(/Z|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
    return `newest: ${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  };

  return (
    <div className="space-y-6" data-testid="admin-data-operations-page">
      <div>
        <h1 className="text-2xl font-bold">Intelligence Data Operations</h1>
        <p className="text-muted-foreground">Monitor data ingestion pipelines and quality</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Newspaper className="w-5 h-5 text-blue-500" />
              <span className="text-sm text-muted-foreground">Total News</span>
            </div>
            <p className="text-3xl font-bold">{newsIngestion.total || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="border-0 bg-gradient-to-br from-emerald-500/10 to-green-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-emerald-500" />
              <span className="text-sm text-muted-foreground">Total Tenders</span>
            </div>
            <p className="text-3xl font-bold">{tenderCollection.total || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="border-0 bg-gradient-to-br from-orange-500/10 to-amber-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-orange-500" />
              <span className="text-sm text-muted-foreground">Projects</span>
            </div>
            <p className="text-3xl font-bold">{entityExtraction.projects?.total || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="border-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-purple-500" />
              <span className="text-sm text-muted-foreground">Companies</span>
            </div>
            <p className="text-3xl font-bold">{entityExtraction.companies?.total || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* News Ingestion */}
      <Card className="border-0 bg-card/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-blue-500" />
            News Ingestion
          </CardTitle>
          <CardDescription>News articles collected from sources</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 rounded-lg bg-muted/30 text-center">
              <p className="text-2xl font-bold text-green-500">{newsIngestion.today || 0}</p>
              <p className="text-sm text-muted-foreground">Today</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/30 text-center">
              <p className="text-2xl font-bold text-blue-500">{newsIngestion.last_7_days || 0}</p>
              <p className="text-sm text-muted-foreground">Last 7 Days</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/30 text-center">
              <p className="text-2xl font-bold text-purple-500">{newsIngestion.last_30_days || 0}</p>
              <p className="text-sm text-muted-foreground">Last 30 Days</p>
            </div>
          </div>
          
          <h4 className="font-medium mb-3">By Source</h4>
          <div className="space-y-2">
            {Object.entries(newsIngestion.by_source || {})
              .sort((a, b) => sourceCount(b[1]) - sourceCount(a[1])).slice(0, 10).map(([source, info]) => {
              const count = sourceCount(info);
              const total = newsIngestion.total || 1;
              const percentage = (count / total) * 100;
              return (
                <div key={source} className="space-y-1">
                  <div className="flex items-center justify-between text-sm gap-2">
                    <span className="truncate">{source}</span>
                    <span className="shrink-0">{count} <span className="text-xs text-muted-foreground">· {newestLabel(info)}</span></span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tender Collection */}
      <Card className="border-0 bg-card/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-500" />
            Tender Collection
          </CardTitle>
          <CardDescription>Tenders collected from government portals</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 rounded-lg bg-muted/30 text-center">
              <p className="text-2xl font-bold text-green-500">{tenderCollection.today || 0}</p>
              <p className="text-sm text-muted-foreground">Today</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/30 text-center">
              <p className="text-2xl font-bold text-blue-500">{tenderCollection.last_7_days || 0}</p>
              <p className="text-sm text-muted-foreground">Last 7 Days</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/30 text-center">
              <p className="text-2xl font-bold text-purple-500">{tenderCollection.last_30_days || 0}</p>
              <p className="text-sm text-muted-foreground">Last 30 Days</p>
            </div>
          </div>
          
          <h4 className="font-medium mb-3">By Portal</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(tenderCollection.by_portal || {}).map(([portal, info]) => (
              <div key={portal} className="p-3 rounded-lg bg-muted/30 text-center">
                <Globe className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
                <p className="text-lg font-bold">{sourceCount(info)}</p>
                <p className="text-xs text-muted-foreground truncate">{portal}</p>
                <p className="text-[11px] text-muted-foreground">{newestLabel(info)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Entity Extraction */}
      <Card className="border-0 bg-card/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            Entity Extraction
          </CardTitle>
          <CardDescription>Projects and companies extracted from news</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 rounded-lg bg-muted/30">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-5 h-5 text-orange-500" />
                <span className="font-medium">Projects</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-2xl font-bold">{entityExtraction.projects?.total || 0}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-500">+{entityExtraction.projects?.last_30_days || 0}</p>
                  <p className="text-sm text-muted-foreground">Last 30 Days</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 rounded-lg bg-muted/30">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-purple-500" />
                <span className="font-medium">Companies</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-2xl font-bold">{entityExtraction.companies?.total || 0}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-500">+{entityExtraction.companies?.last_30_days || 0}</p>
                  <p className="text-sm text-muted-foreground">Last 30 Days</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WO-36 — the old "Data Quality" card showed a distinct-titles ratio
          labelled "deduplication" and two error counters nothing ever wrote.
          The real checks are the maintenance jobs below. */}
      <p className="text-xs text-muted-foreground" data-testid="data-quality-note">
        "Today / 7 days / 30 days" count records by the date they entered Mid2Bid. Duplicate and data-quality checks
        are the maintenance jobs below; automatic quality counters are not measured yet.
      </p>

      <MaintenanceJobs />
    </div>
  );
}

const MAINTENANCE_JOBS = [
  {
    id: 'deadline_eod',
    title: 'Fix date-only tender deadlines',
    blurb: "Some portals publish a closing date with no time on it. Those were stored as the first second of that day, so a tender looked closed while bidders still had the whole day. This moves them to 23:59:59 in the portal's own timezone.",
    confirm: 'APPLY-DEADLINE-EOD',
    foundKey: 'at_local_midnight',
    writtenKey: 'written',
    breakdownKey: 'per_source',
    foundLabel: 'Deadlines stored a day early',
    writtenLabel: 'Deadlines corrected',
    canApply: true,
  },
  {
    id: 'tender_timezones',
    title: 'Re-check portal timezone conversion',
    blurb: 'A one-off correction that already ran over every tender. It skips anything it has already handled, so it should report nothing left to do — which is how you confirm it has stayed done.',
    confirm: 'APPLY-TENDER-TIMEZONES',
    foundKey: 'to_shift',
    writtenKey: 'shifted',
    breakdownKey: 'per_country',
    foundLabel: 'Records still to shift',
    writtenLabel: 'Records shifted',
    canApply: true,
  },
  {
    id: 'dedupe_refs',
    title: 'Duplicate tender references',
    blurb: 'Finds tenders stored more than once under different generated references. Previewing is safe and changes nothing. Applying it DELETES rows, so it is deliberately not available from this screen.',
    confirm: 'APPLY-DEDUPE-REFS',
    foundKey: 'to_delete',
    writtenKey: 'deleted',
    breakdownKey: null,
    foundLabel: 'Duplicate rows found',
    writtenLabel: 'Rows deleted',
    canApply: false,
  },
  {
    id: 'duplicate_contacts',
    title: 'Duplicate CRM contacts',
    blurb: 'Two contacts in one company sharing an email address block the duplicate-protection index from being switched on. Previewing is safe and changes nothing; clean the listed rows, then a restart lets the protection take.',
    confirm: null,
    foundKey: 'duplicate_rows',
    writtenKey: null,
    breakdownKey: 'per_tenant',
    foundLabel: 'Duplicate contact rows',
    writtenLabel: '',
    canApply: false,
  },
];

function MaintenanceJobs() {
  const { getToken } = useAuth();
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(null);
  const [pending, setPending] = useState(null);

  const call = async (job, apply) => {
    setBusy(job.id + (apply ? ':apply' : ':preview'));
    try {
      const res = await fetch(`${API_URL}/api/admin/maintenance/run`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          apply
            ? { script: job.id, apply: true, confirm: job.confirm }
            : { script: job.id, apply: false }
        ),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || `HTTP ${res.status}`);
      setResults((prev) => ({ ...prev, [job.id]: { ...json, applied: apply } }));
      if (apply) {
        toast.success(`${job.title}: ${json[job.writtenKey] ?? 0} record(s) written.`);
      } else {
        toast.success(`${job.title}: ${json[job.foundKey] ?? 0} record(s) found. Nothing written.`);
      }
    } catch (err) {
      toast.error(`${job.title} failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="border-0 bg-card/50" data-testid="admin-maintenance-jobs">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="w-5 h-5 text-blue-500" />
          Data Corrections
        </CardTitle>
        <CardDescription>
          Preview first — it reads the database and writes nothing. Apply only becomes
          available once a preview has found something to fix.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {MAINTENANCE_JOBS.map((job) => {
          const r = results[job.id];
          const found = r ? Number(r[job.foundKey] ?? 0) : null;
          const written = r && r.applied ? Number(r[job.writtenKey] ?? 0) : null;
          const breakdown = job.breakdownKey && r ? r[job.breakdownKey] : null;
          const previewing = busy === `${job.id}:preview`;
          const applying = busy === `${job.id}:apply`;
          const applyEnabled = job.canApply && found !== null && found > 0 && !busy;

          return (
            <div key={job.id} className="p-4 rounded-lg bg-muted/40" data-testid={`maintenance-job-${job.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[16rem]">
                  <p className="font-semibold">{job.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">{job.blurb}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!!busy}
                    onClick={() => call(job, false)}
                    data-testid={`maintenance-preview-${job.id}`}
                  >
                    {previewing ? (
                      <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Database className="w-4 h-4 mr-2" />
                    )}
                    Preview
                  </Button>
                  {job.canApply && (
                    <Button
                      size="sm"
                      disabled={!applyEnabled}
                      onClick={() => setPending(job)}
                      data-testid={`maintenance-apply-${job.id}`}
                    >
                      {applying ? <RefreshCcw className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Apply
                    </Button>
                  )}
                </div>
              </div>

              {r && (
                <div className="mt-3 pt-3 border-t border-border/50 text-sm space-y-1">
                  <p>
                    <span className="text-muted-foreground">{job.foundLabel}: </span>
                    <span className="font-semibold">{found}</span>
                    {written !== null && (
                      <>
                        <span className="text-muted-foreground"> · {job.writtenLabel}: </span>
                        <span className="font-semibold text-green-500">{written}</span>
                      </>
                    )}
                    {!r.applied && (
                      <Badge variant="outline" className="ml-2">preview — nothing written</Badge>
                    )}
                  </p>
                  {breakdown && Object.keys(breakdown).length > 0 && (
                    <p className="text-muted-foreground">
                      {Object.entries(breakdown)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(' · ')}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Database: {r.db_name || 'unknown'}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>

      <Dialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent data-testid="maintenance-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Apply this correction?</DialogTitle>
            <DialogDescription>
              {pending && (
                <>
                  <span className="block font-semibold text-foreground">{pending.title}</span>
                  <span className="block mt-2">
                    This will write to{' '}
                    <span className="font-semibold">
                      {results[pending.id]?.[pending.foundKey] ?? 0}
                    </span>{' '}
                    record(s) in database{' '}
                    <span className="font-semibold">
                      {results[pending.id]?.db_name || 'unknown'}
                    </span>
                    . Running it again afterwards changes nothing further.
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const job = pending;
                setPending(null);
                if (job) call(job, true);
              }}
              data-testid="maintenance-confirm-apply"
            >
              Yes, apply it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
